import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config/config';

// ─── Constants ────────────────────────────────────────────────────────────────
const ADMIN_API = `${API_BASE_URL}/api/admin-console/lead-ambassadors`;

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu','Delhi',
  'Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry',
].sort();

const COUNTRIES = ['India','United States','United Kingdom','Canada','Australia','Singapore','Germany','Other'];

const EMPTY_FORM = {
  name: '', email: '', phone: '', designation: '',
  address: '', state: '', country: 'India', status: 'ACTIVE',
  partner_type: 'LEAD_AMBASSADOR',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getAuthHeader = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const t = u.token || u.session_token || localStorage.getItem('authToken') || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
};

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputSt = {
  width: '100%', padding: '9px 12px', fontSize: 13, fontWeight: 600,
  color: '#0F172A', background: '#F8FAFC', border: '1.5px solid #E2E8F0',
  borderRadius: 10, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color .15s',
};
const btnPrimary = {
  padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
  background: 'var(--primary-blue,#4F46E5)', color: '#fff',
  fontSize: 13, fontWeight: 800, letterSpacing: '.03em',
  boxShadow: '0 3px 10px rgba(79,70,229,.25)',
};
const btnSecondary = {
  padding: '9px 16px', borderRadius: 9, border: '1.5px solid #E2E8F0',
  background: '#F8FAFC', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
const btnDanger = { ...btnPrimary, background: '#DC2626', boxShadow: '0 3px 10px rgba(220,38,38,.25)' };
const btnSuccess = { ...btnPrimary, background: '#16A34A', boxShadow: '0 3px 10px rgba(22,163,74,.25)' };

// ─── Sub-components ───────────────────────────────────────────────────────────
const Badge = ({ status }) => (
  <span style={{
    padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: status === 'ACTIVE' ? '#D1FAE5' : '#FEE2E2',
    color:      status === 'ACTIVE' ? '#065F46' : '#991B1B',
    border:     `1px solid ${status === 'ACTIVE' ? '#A7F3D0' : '#FECACA'}`,
  }}>{status}</span>
);

const PartnerTypeBadge = ({ type }) => {
  const isLA = type === 'LEAD_AMBASSADOR' || !type;
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: isLA ? '#DBEAFE' : '#FEF3C7',
      color: isLA ? '#1D4ED8' : '#92400E',
      border: `1px solid ${isLA ? '#BFDBFE' : '#FDE68A'}`,
      whiteSpace: 'nowrap',
    }}>
      {isLA ? 'Lead Ambassador' : 'Sales Executive'}
    </span>
  );
};

const Modal = ({ title, onClose, size = 540, children }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  }}>
    <div style={{
      background: '#fff', borderRadius: 20, width: '90%', maxWidth: size,
      boxShadow: '0 24px 72px rgba(0,0,0,.22)',
      display: 'flex', flexDirection: 'column', maxHeight: '90vh',
    }}>
      <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0F172A' }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94A3B8', lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>
      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>{children}</div>
    </div>
  </div>
);

const FieldRow = ({ label, required, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>
      {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const SummaryCard = ({ icon, label, value, bg, color }) => (
  <div style={{ background: bg, borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,.06)', display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ fontSize: 24 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 12, fontWeight: 700, color, opacity: .75, letterSpacing: '.03em' }}>{label}</div>
  </div>
);

const Pagination = ({ page, pages, total, limit, onChange }) => {
  if (pages <= 1) return null;
  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 10 }}>
      <span style={{ fontSize: 12, color: '#64748B' }}>Showing {from}–{to} of {total}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onChange(1)} disabled={page === 1} style={{ ...btnSecondary, padding: '6px 10px', opacity: page===1?.5:1 }}>«</button>
        <button onClick={() => onChange(page-1)} disabled={page === 1} style={{ ...btnSecondary, padding: '6px 10px', opacity: page===1?.5:1 }}>‹</button>
        {[...Array(Math.min(5, pages))].map((_, i) => {
          const p = Math.max(1, Math.min(pages-4, page-2)) + i;
          if (p > pages) return null;
          return (
            <button key={p} onClick={() => onChange(p)} style={{
              ...btnSecondary, padding: '6px 12px', fontWeight: 800,
              background: p===page ? 'var(--primary-blue,#4F46E5)' : '#F8FAFC',
              color: p===page ? '#fff' : '#475569',
              borderColor: p===page ? 'transparent' : '#E2E8F0',
            }}>{p}</button>
          );
        })}
        <button onClick={() => onChange(page+1)} disabled={page===pages} style={{ ...btnSecondary, padding: '6px 10px', opacity: page===pages?.5:1 }}>›</button>
        <button onClick={() => onChange(pages)} disabled={page===pages} style={{ ...btnSecondary, padding: '6px 10px', opacity: page===pages?.5:1 }}>»</button>
      </div>
    </div>
  );
};

// ─── Ambassador Form (Create / Edit) ─────────────────────────────────────────
const AmbassadorForm = ({ initial, isEdit, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [phoneError, setPhoneError] = useState('');
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(p => ({ ...p, phone: digits }));
    if (digits.length === 0)        setPhoneError('Phone number is required.');
    else if (digits.length < 10)    setPhoneError(`Enter all 10 digits (${digits.length}/10 entered).`);
    else if (/^[0-4]/.test(digits)) setPhoneError('Invalid number — must start with 5–9.');
    else                            setPhoneError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const digits = (form.phone || '').replace(/\D/g, '');
    if (digits.length !== 10) { setPhoneError('Enter a valid 10-digit phone number.'); return; }
    if (/^[0-4]/.test(digits)) { setPhoneError('Invalid number — must start with 5–9.'); return; }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      {isEdit && (
        <div style={{ padding: '8px 14px', borderRadius: 10, background: '#EDE9FE', border: '1px solid #C4B5FD', marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#5B21B6', fontWeight: 600 }}>
            Ambassador ID <strong>{initial?.ambassador_id}</strong> is read-only and cannot be changed.
          </p>
        </div>
      )}
      {!isEdit && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Partner Type</label>
          <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: 10, padding: 4 }}>
            {[
              { value: 'LEAD_AMBASSADOR', label: 'Lead Ambassador' },
              { value: 'SALES_EXECUTIVE', label: 'Sales Executive' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(p => ({ ...p, partner_type: opt.value }))}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 700, transition: 'all .15s',
                  background: form.partner_type === opt.value ? '#fff' : 'transparent',
                  color: form.partner_type === opt.value ? '#4F46E5' : '#64748B',
                  boxShadow: form.partner_type === opt.value ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
                }}
              >{opt.label}</button>
            ))}
          </div>
        </div>
      )}
      {!isEdit && (
        <div style={{ padding: '8px 14px', borderRadius: 10, background: '#EDE9FE', border: '1px solid #C4B5FD', marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#5B21B6', fontWeight: 600 }}>
            {form.partner_type === 'SALES_EXECUTIVE'
              ? <>Partner ID will be auto-generated as <strong>EXISALES-001</strong> (global sequence).</>
              : <>Ambassador ID will be auto-generated as <strong>EXITSG-{'<STATE>'}-001</strong> based on the selected state.</>
            }
          </p>
        </div>
      )}
      {error && <div style={{ padding: '10px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Full Name" required>
            <input style={inputSt} value={form.name} onChange={f('name')} placeholder="Ramesh Kumar" />
          </FieldRow>
        </div>
        <FieldRow label="Designation" required>
          <input style={inputSt} value={form.designation} onChange={f('designation')} placeholder="Regional Partner" />
        </FieldRow>
        <FieldRow label="Email Address" required>
          <input style={inputSt} type="email" value={form.email} onChange={f('email')} placeholder="ramesh@example.com" />
        </FieldRow>
        <FieldRow label="Phone Number" required>
          <input
            style={{ ...inputSt, borderColor: phoneError ? '#EF4444' : '#E2E8F0' }}
            value={form.phone}
            onChange={handlePhoneChange}
            placeholder="10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
          />
          {phoneError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
              <span style={{ fontSize: 13, color: '#EF4444' }}>⚠</span>
              <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>{phoneError}</span>
            </div>
          )}
          {!phoneError && form.phone.length === 10 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
              <span style={{ fontSize: 13, color: '#16A34A' }}>✓</span>
              <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 600 }}>Valid phone number</span>
            </div>
          )}
        </FieldRow>
        {isEdit && (
          <FieldRow label="Status">
            <select style={{ ...inputSt, cursor: 'pointer', appearance: 'none' }} value={form.status} onChange={f('status')}>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
          </FieldRow>
        )}
        <div style={{ gridColumn: '1/-1' }}>
          <FieldRow label="Address" required>
            <textarea style={{ ...inputSt, resize: 'vertical', minHeight: 64 }} value={form.address} onChange={f('address')} placeholder="Full address" />
          </FieldRow>
        </div>
        <FieldRow label="State" required={!isEdit && form.partner_type === 'LEAD_AMBASSADOR'}>
          <select style={{ ...inputSt, cursor: 'pointer', appearance: 'none' }} value={form.state} onChange={f('state')}>
            <option value="">Select State{!isEdit && form.partner_type === 'SALES_EXECUTIVE' ? ' (optional)' : ''}</option>
            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Country">
          <select style={{ ...inputSt, cursor: 'pointer', appearance: 'none' }} value={form.country} onChange={f('country')}>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FieldRow>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8, paddingTop: 16, borderTop: '1px solid #F1F5F9' }}>
        <button type="button" onClick={onCancel} style={btnSecondary}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? .7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Partner'}
        </button>
      </div>
    </form>
  );
};

// ─── Profile View ─────────────────────────────────────────────────────────────
const ProfileView = ({ amb, onClose, onEdit }) => (
  <Modal title="Ambassador Profile" onClose={onClose} size={620}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {[
        ['Ambassador ID', amb.ambassador_id, true],
        ['Partner Type', <PartnerTypeBadge type={amb.partner_type} />],
        ['Status', <Badge status={amb.status} />],
        ['Full Name', amb.full_name || amb.name],
        ['Designation', amb.designation || '—'],
        ['Email', amb.email],
        ['Phone', amb.phone || '—'],
        ['State', amb.state || '—'],
        ['Country', amb.country || '—'],
        ['Assigned Institutions', amb.institution_count ?? 0],
        ['Created By', amb.created_by || '—'],
        ['Created At', fmtDate(amb.created_at)],
        ['Updated At', fmtDate(amb.updated_at)],
      ].map(([label, value, mono]) => (
        <div key={label} style={{ background: '#F8FAFC', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
        </div>
      ))}
      {amb.address && (
        <div style={{ gridColumn: '1/-1', background: '#F8FAFC', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Address</div>
          <div style={{ fontSize: 13, color: '#0F172A' }}>{amb.address}</div>
        </div>
      )}
    </div>
    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
      <button onClick={() => { onClose(); onEdit(amb); }} style={btnPrimary}>Edit Ambassador</button>
      <button onClick={onClose} style={btnSecondary}>Close</button>
    </div>
  </Modal>
);

// ─── Assigned Institutions Modal ──────────────────────────────────────────────
const InstitutionsModal = ({ ambassadorId, ambassadorName, onClose }) => {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    axios.get(`${ADMIN_API}/${ambassadorId}/institutions`, { headers: getAuthHeader() })
      .then(r => setData(r.data?.institutions || []))
      .catch(() => setError('Failed to load institutions'))
      .finally(() => setLoading(false));
  }, [ambassadorId]);

  return (
    <Modal title={`Assigned Institutions — ${ambassadorName}`} onClose={onClose} size={800}>
      <p style={{ margin: '0 0 16px', color: '#64748B', fontSize: 13 }}>Total: <strong style={{ color: '#0F172A' }}>{data.length}</strong></p>
      {loading ? <div style={{ textAlign: 'center', color: '#64748B', padding: 32 }}>Loading…</div>
        : error ? <div style={{ color: '#DC2626', padding: 16, background: '#FEF2F2', borderRadius: 10 }}>{error}</div>
        : data.length === 0 ? <div style={{ textAlign: 'center', color: '#94A3B8', padding: 32 }}>No institutions assigned yet.</div>
        : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Institution Name','Code','Email','Phone','State','Registered','Status'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #E2E8F0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((inst, i) => (
                  <tr key={inst.id} style={{ borderBottom: i < data.length-1 ? '1px solid #F1F5F9' : 'none' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0F172A' }}>{inst.institution_name}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#4F46E5' }}>{inst.institution_code}</td>
                    <td style={{ padding: '10px 12px', color: '#475569' }}>{inst.email}</td>
                    <td style={{ padding: '10px 12px', color: '#475569' }}>{inst.phone || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{inst.state || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#94A3B8', whiteSpace: 'nowrap' }}>{fmtDate(inst.registration_date)}</td>
                    <td style={{ padding: '10px 12px' }}><Badge status={inst.tenant_status || 'ACTIVE'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Modal>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const LeadAmbassadorDashboard = () => {
  const navigate = useNavigate();
  // List state
  const [ambassadors,  setAmbassadors]  = useState([]);
  const [pagination,   setPagination]   = useState({ page: 1, pages: 1, total: 0, limit: 20 });
  const [loading,      setLoading]      = useState(true);
  const [listError,    setListError]    = useState('');

  // Filters / sorting
  const [search,           setSearch]           = useState('');
  const [filterState,      setFilterState]      = useState('');
  const [filterCountry,    setFilterCountry]    = useState('');
  const [filterStatus,     setFilterStatus]     = useState('');
  const [filterDesig,      setFilterDesig]      = useState('');
  const [filterPartnerType,setFilterPartnerType]= useState('');
  const [dateFrom,         setDateFrom]         = useState('');
  const [dateTo,           setDateTo]           = useState('');
  const [sortBy,           setSortBy]           = useState('created_at');
  const [sortDir,          setSortDir]          = useState('desc');
  const [page,             setPage]             = useState(1);
  const searchTimer = useRef(null);

  // Modals
  const [showCreate,   setShowCreate]   = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [viewTarget,   setViewTarget]   = useState(null);
  const [instTarget,   setInstTarget]   = useState(null); // { ambassadorId, ambassadorName }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null); // { amb, newStatus }
  const [formSaving,   setFormSaving]   = useState(false);
  const [formError,    setFormError]    = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const totalActive   = ambassadors.filter(a => a.status === 'ACTIVE').length;
  const totalInactive = ambassadors.filter(a => a.status === 'INACTIVE').length;
  const totalLA       = ambassadors.filter(a => !a.partner_type || a.partner_type === 'LEAD_AMBASSADOR').length;
  const totalSE       = ambassadors.filter(a => a.partner_type === 'SALES_EXECUTIVE').length;

  const fetchList = useCallback(async (pg = page) => {
    setLoading(true);
    setListError('');
    try {
      const params = { page: pg, limit: 20, sort_by: sortBy, sort_dir: sortDir };
      if (search)             params.search        = search;
      if (filterState)        params.state         = filterState;
      if (filterCountry)      params.country       = filterCountry;
      if (filterStatus)       params.status        = filterStatus;
      if (filterDesig)        params.designation   = filterDesig;
      if (filterPartnerType)  params.partner_type  = filterPartnerType;
      if (dateFrom)           params.date_from     = dateFrom;
      if (dateTo)             params.date_to       = dateTo;

      const res = await axios.get(ADMIN_API, { params, headers: getAuthHeader() });
      setAmbassadors(res.data?.ambassadors || []);
      setPagination(res.data?.pagination   || { page: pg, pages: 1, total: 0, limit: 20 });
    } catch (err) {
      setListError(err.response?.data?.error || 'Failed to load ambassadors');
    } finally { setLoading(false); }
  }, [page, search, filterState, filterCountry, filterStatus, filterDesig, filterPartnerType, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => { fetchList(page); }, [fetchList, page]);

  const handleSearchChange = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); }, 600);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };

  const resetFilters = () => {
    setSearch(''); setFilterState(''); setFilterCountry('');
    setFilterStatus(''); setFilterDesig(''); setFilterPartnerType('');
    setDateFrom(''); setDateTo('');
    setSortBy('created_at'); setSortDir('desc'); setPage(1);
  };

  // Export
  const handleExport = (fmt) => {
    const url = `${ADMIN_API}/export/${fmt}`;
    const tok = getAuthHeader().Authorization || '';
    const a = document.createElement('a');
    a.href = `${url}?token=${encodeURIComponent(tok.replace('Bearer ',''))}`;
    a.target = '_blank';
    a.click();
  };

  // Create
  const handleCreate = async (form) => {
    setFormSaving(true); setFormError('');
    try {
      await axios.post(ADMIN_API, form, { headers: getAuthHeader() });
      setShowCreate(false); setPage(1); fetchList(1);
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to create'); }
    finally { setFormSaving(false); }
  };

  // Edit
  const handleUpdate = async (form) => {
    setFormSaving(true); setFormError('');
    try {
      await axios.put(`${ADMIN_API}/${editTarget.ambassador_id}`, form, { headers: getAuthHeader() });
      setEditTarget(null); fetchList(page);
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to update'); }
    finally { setFormSaving(false); }
  };

  // Status toggle
  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    setActionLoading(true);
    const ep = statusTarget.newStatus === 'ACTIVE' ? 'activate' : 'deactivate';
    try {
      await axios.patch(`${ADMIN_API}/${statusTarget.amb.ambassador_id}/${ep}`, {}, { headers: getAuthHeader() });
      setStatusTarget(null); fetchList(page);
    } catch { alert('Failed to update status'); }
    finally { setActionLoading(false); }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await axios.delete(`${ADMIN_API}/${deleteTarget.ambassador_id}`, { headers: getAuthHeader() });
      setDeleteTarget(null); setPage(1); fetchList(1);
    } catch { alert('Failed to delete'); }
    finally { setActionLoading(false); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{ color: '#CBD5E1', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#4F46E5', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const TH = ({ col, label, noSort }) => (
    <th
      onClick={noSort ? undefined : () => handleSort(col)}
      style={{
        padding: '11px 14px', textAlign: 'left', fontWeight: 700, color: '#64748B',
        fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em',
        whiteSpace: 'nowrap', borderBottom: '1px solid #E2E8F0',
        cursor: noSort ? 'default' : 'pointer', userSelect: 'none',
        background: '#F8FAFC',
      }}
    >
      {label}{!noSort && <SortIcon col={col} />}
    </th>
  );

  return (
    <div style={{ padding: '28px 28px 60px', minHeight: '100vh', background: '#F8FAFC', fontFamily: 'inherit' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, borderRadius: 10, border: '1.5px solid #E2E8F0',
              background: '#fff', color: '#64748B', cursor: 'pointer',
              transition: 'all 0.15s', marginTop: 2, flexShrink: 0
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#0F172A'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748B'; }}
            title="Go Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#0F172A', letterSpacing: '-.02em' }}>Partner Directory</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B', fontWeight: 500 }}>
              Manage Lead Ambassadors and Sales Executives responsible for institution onboarding.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => fetchList(page)} style={btnSecondary}>↻ Refresh</button>
          <button onClick={() => handleExport('csv')} style={btnSecondary}>↓ CSV</button>
          <button onClick={() => handleExport('excel')} style={btnSecondary}>↓ Excel</button>
          <button onClick={() => { setShowCreate(true); setFormError(''); }} style={btnPrimary}>+ Add Partner</button>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 24 }}>
        <SummaryCard icon="🤝" label="Total"      value={pagination.total} bg="#EDE9FE" color="#5B21B6" />
        <SummaryCard icon="✅" label="Active"     value={totalActive}      bg="#D1FAE5" color="#065F46" />
        <SummaryCard icon="⏸" label="Inactive"   value={totalInactive}    bg="#FEE2E2" color="#991B1B" />
        <SummaryCard icon="🏫" label="Lead Amb."  value={totalLA}          bg="#DBEAFE" color="#1D4ED8" />
        <SummaryCard icon="💼" label="Sales Exec." value={totalSE}         bg="#FEF3C7" color="#92400E" />
      </div>

      {/* ── Filters ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
        {/* Row 1: search + dropdowns + designation — equal columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <input style={inputSt} placeholder="Search ID, name, email, phone…" value={search} onChange={e => handleSearchChange(e.target.value)} />
          <select style={{ ...inputSt, cursor: 'pointer' }} value={filterState} onChange={e => { setFilterState(e.target.value); setPage(1); }}>
            <option value="">All States</option>
            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ ...inputSt, cursor: 'pointer' }} value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(1); }}>
            <option value="">All Countries</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{ ...inputSt, cursor: 'pointer' }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <select style={{ ...inputSt, cursor: 'pointer' }} value={filterPartnerType} onChange={e => { setFilterPartnerType(e.target.value); setPage(1); }}>
            <option value="">All Types</option>
            <option value="LEAD_AMBASSADOR">Lead Ambassador</option>
            <option value="SALES_EXECUTIVE">Sales Executive</option>
          </select>
          <input style={inputSt} placeholder="Designation…" value={filterDesig} onChange={e => { setFilterDesig(e.target.value); setPage(1); }} />
        </div>
        {/* Row 2: date range + clear — aligned left, compact */}
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr auto', gap: 10, alignItems: 'center', maxWidth: 560 }}>
          <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' }}>From</label>
          <input type="date" style={inputSt} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
          <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' }}>To</label>
          <input type="date" style={inputSt} value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
          <button onClick={resetFilters} style={{ ...btnSecondary, fontSize: 12, whiteSpace: 'nowrap' }}>Clear Filters</button>
        </div>
      </div>

      {/* ── Error ── */}
      {listError && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
          {listError}
        </div>
      )}

      {/* ── Table ── */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(0,0,0,.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 56, textAlign: 'center', color: '#64748B' }}>Loading ambassadors…</div>
        ) : ambassadors.length === 0 ? (
          <div style={{ padding: 56, textAlign: 'center', color: '#94A3B8' }}>No ambassadors match your filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <TH col="ambassador_id" label="Ambassador ID" />
                  <TH col="partner_type"  label="Type" noSort />
                  <TH col="name"          label="Full Name" />
                  <TH col="designation"   label="Designation" />
                  <TH col="phone"         label="Phone" noSort />
                  <TH col="email"         label="Email" noSort />
                  <TH col="state"         label="State" />
                  <TH col="country"       label="Country" />
                  <TH col="status"        label="Status" />
                  <TH col="institution_count" label="Colleges" />
                  <TH col="created_at"    label="Created" />
                  <TH col="_actions"      label="Actions" noSort />
                </tr>
              </thead>
              <tbody>
                {ambassadors.map((amb, idx) => (
                  <tr key={amb.ambassador_id}
                    style={{ borderBottom: idx < ambassadors.length-1 ? '1px solid #F1F5F9' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontWeight: 800, color: '#4F46E5', whiteSpace: 'nowrap' }}>{amb.ambassador_id}</td>
                    <td style={{ padding: '12px 14px' }}><PartnerTypeBadge type={amb.partner_type} /></td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>{amb.full_name || amb.name}</td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>{amb.designation || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{amb.phone || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>{amb.email}</td>
                    <td style={{ padding: '12px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{amb.state || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>{amb.country || '—'}</td>
                    <td style={{ padding: '12px 14px' }}><Badge status={amb.status} /></td>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 700, color: '#0F172A' }}>
                      <button
                        onClick={() => setInstTarget({ ambassadorId: amb.ambassador_id, ambassadorName: amb.name })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5', fontWeight: 700, fontSize: 13 }}
                      >{amb.institution_count || 0}</button>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#94A3B8', whiteSpace: 'nowrap' }}>{fmtDate(amb.created_at)}</td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setViewTarget(amb)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 12 }}>View</button>
                        <button onClick={() => { setEditTarget(amb); setFormError(''); }} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 12 }}>Edit</button>
                        {amb.status === 'ACTIVE'
                          ? <button onClick={() => setStatusTarget({ amb, newStatus: 'INACTIVE' })} style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Deactivate</button>
                          : <button onClick={() => setStatusTarget({ amb, newStatus: 'ACTIVE' })} style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Activate</button>
                        }
                        <button onClick={() => setDeleteTarget(amb)} style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: '#FEE2E2', color: '#991B1B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                        <button onClick={() => setInstTarget({ ambassadorId: amb.ambassador_id, ambassadorName: amb.name })} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 12 }}>Colleges</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={pagination.page} pages={pagination.pages} total={pagination.total} limit={pagination.limit} onChange={(p) => setPage(p)} />

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="Add New Partner" onClose={() => setShowCreate(false)}>
          <AmbassadorForm
            initial={EMPTY_FORM} isEdit={false}
            onSave={handleCreate} onCancel={() => setShowCreate(false)}
            saving={formSaving} error={formError}
          />
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editTarget && (
        <Modal title={`Edit Ambassador — ${editTarget.ambassador_id}`} onClose={() => setEditTarget(null)}>
          <AmbassadorForm
            initial={editTarget} isEdit={true}
            onSave={handleUpdate} onCancel={() => setEditTarget(null)}
            saving={formSaving} error={formError}
          />
        </Modal>
      )}

      {/* ── Profile View Modal ── */}
      {viewTarget && (
        <ProfileView
          amb={viewTarget}
          onClose={() => setViewTarget(null)}
          onEdit={(amb) => { setEditTarget(amb); setFormError(''); }}
        />
      )}

      {/* ── Institutions Modal ── */}
      {instTarget && (
        <InstitutionsModal
          ambassadorId={instTarget.ambassadorId}
          ambassadorName={instTarget.ambassadorName}
          onClose={() => setInstTarget(null)}
        />
      )}

      {/* ── Status Confirm ── */}
      {statusTarget && (
        <Modal title={`${statusTarget.newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate'} Ambassador`} onClose={() => setStatusTarget(null)} size={440}>
          <p style={{ margin: '0 0 20px', color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
            {statusTarget.newStatus === 'ACTIVE'
              ? `Activating ${statusTarget.amb.name} will allow new institution registrations using their ID.`
              : `Deactivating ${statusTarget.amb.name} will prevent new institution registrations. Existing mappings remain unchanged.`}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setStatusTarget(null)} style={btnSecondary}>Cancel</button>
            <button onClick={confirmStatusChange} disabled={actionLoading} style={statusTarget.newStatus === 'ACTIVE' ? btnSuccess : btnDanger}>
              {actionLoading ? 'Updating…' : `Yes, ${statusTarget.newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate'}`}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <Modal title="Delete Ambassador" onClose={() => setDeleteTarget(null)} size={440}>
          <p style={{ margin: '0 0 6px', color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{deleteTarget.name}</strong> ({deleteTarget.ambassador_id})?
          </p>
          <p style={{ margin: '0 0 20px', fontSize: 12, color: '#94A3B8' }}>This is a soft delete. The record will be retained for audit purposes.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setDeleteTarget(null)} style={btnSecondary}>Cancel</button>
            <button onClick={confirmDelete} disabled={actionLoading} style={btnDanger}>
              {actionLoading ? 'Deleting…' : 'Yes, Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LeadAmbassadorDashboard;
